import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Mic, Search, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "../ui/dialog";
import { buildIndex, search, snippet } from "@/lib/searchIndex";
import { useCustomFaqs } from "@/lib/customFaqs";
import { scrollToId } from "@/lib/scroll";
import { useLang, useT } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GROUPS = [["pages", "Pagine e sezioni"], ["products", "Prodotti"], ["projects", "Progetti realizzati"], ["faq", "Domande frequenti"], ["sheets", "Schede tecniche PDF"]];

const Result = ({ r, q, onPick }) => (
  <li>
    <button onClick={() => onPick(r.to)} className="group flex w-full items-start justify-between gap-4 rounded-lg px-3 py-3 text-left transition-colors duration-200 hover:bg-white/5 focus:bg-white/5 focus:outline-none" data-testid={`search-result-${r.id}`}>
      <span>
        <span className="block font-display text-base font-semibold text-[#F7F5F0]">{r.title}</span>
        {r.text && <span className="mt-1 block text-sm leading-relaxed text-[#CFC8BC]">{snippet(r.text, q)}</span>}
      </span>
      <ArrowUpRight className="mt-1 h-4 w-4 shrink-0 text-[#D98A52] transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
    </button>
  </li>
);

const FALLBACK = ["travi curve", "x-lam", "casa in legno", "perline", "stabilimenti balneari", "classi di resistenza"];

const SPEECH_LANG = { it: "it-IT", en: "en-GB", fr: "fr-FR" };
const Recognition = typeof window !== "undefined" ? window.SpeechRecognition || window.webkitSpeechRecognition : null;

const useVoice = (lang, onText, t, active) => {
  const [listening, setListening] = useState(false);
  const rec = useRef(null);
  useEffect(() => { if (!active) rec.current?.abort(); }, [active]);
  useEffect(() => () => { rec.current?.abort(); }, []);
  const toggle = () => {
    if (listening) { rec.current?.stop(); return; }
    const r = new Recognition();
    r.lang = SPEECH_LANG[lang] ?? "it-IT";
    r.interimResults = true;
    r.maxAlternatives = 1;
    r.onresult = (e) => onText(Array.from(e.results).map((x) => x[0].transcript).join(" "));
    r.onerror = (e) => { if (e.error === "not-allowed" || e.error === "service-not-allowed") toast.error(t("search.micDenied", "Consenti l'uso del microfono nel browser per cercare parlando.")); };
    r.onend = () => setListening(false);
    rec.current = r;
    setListening(true);
    r.start();
  };
  return { supported: !!Recognition, listening, toggle };
};

const MicButton = ({ voice, t }) => voice.supported && (
  <button onClick={voice.toggle} className={`relative grid h-9 w-9 shrink-0 place-items-center rounded-full border transition-colors duration-200 ${voice.listening ? "border-[#D98A52] bg-[#D98A52] text-[#14110F]" : "border-white/15 hover:border-[#D98A52]"}`} data-testid="search-mic" aria-pressed={voice.listening} aria-label={voice.listening ? t("search.micStop", "Ferma la ricerca vocale") : t("search.mic", "Cerca parlando")}>
    {voice.listening && <span className="absolute inset-0 animate-ping rounded-full bg-[#D98A52]/40" />}
    <Mic className="relative h-4 w-4" />
  </button>
);

const Suggestions = ({ terms, onPick, t }) => (
  <div className="px-3 pt-5" data-testid="search-suggestions">
    <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#D98A52]">{t("search.popular", "Le ricerche più frequenti")}</p>
    <div className="mt-3 flex flex-wrap gap-2">
      {terms.map((w) => (
        <button key={w} onClick={() => onPick(w)} className="rounded-full border border-white/15 px-4 py-2 text-sm text-[#F7F5F0] transition-colors duration-200 hover:border-[#D98A52] hover:text-[#D98A52]" data-testid={`search-suggestion-${w.replace(/\s+/g, "-")}`}>{w}</button>
      ))}
    </div>
  </div>
);

const Empty = ({ q, onAsk, t }) => (
  <div className="px-3 py-6" data-testid="search-empty">
    <p className="text-sm text-[#CFC8BC]">{t("search.empty2", "Nessun risultato per questa ricerca. Chiedilo direttamente al nostro ufficio tecnico: ti rispondiamo noi.")}</p>
    <button onClick={onAsk} className="btn-amber mt-5" data-testid="search-empty-quote">
      {t("search.ask", "Chiedi un preventivo per")} “{q.trim().slice(0, 40)}” <ArrowRight className="h-4 w-4" />
    </button>
  </div>
);

const Results = ({ results, q, onPick, onAsk, onSuggest, popular, t }) => {
  if (!q.trim()) return <>
    <Suggestions terms={popular} onPick={onSuggest} t={t} />
    <p className="px-3 py-6 text-sm text-[#CFC8BC]" data-testid="search-hint">{t("search.hint", "Cerca prodotti, progetti, domande frequenti e schede tecniche. Esempio: trave curva, X-LAM, perline, stabilimenti balneari.")}</p>
  </>;
  if (!results.length) return <Empty q={q} onAsk={onAsk} t={t} />;
  return GROUPS.map(([g, label]) => {
    const list = results.filter((r) => r.group === g);
    if (!list.length) return null;
    return (
      <section key={g} className="mt-4" data-testid={`search-group-${g}`}>
        <p className="px-3 font-mono text-xs uppercase tracking-[0.2em] text-[#D98A52]">{t(`search.g.${g}`, label)} · {list.length}</p>
        <ul className="mt-1">{list.map((r) => <Result key={r.id} r={r} q={q} onPick={onPick} />)}</ul>
      </section>
    );
  });
};

export const SiteSearch = ({ open, onOpenChange, start }) => {
  const t = useT();
  const { lang } = useLang();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [q, setQ] = useState("");
  const custom = useCustomFaqs(lang, open);
  const index = useMemo(() => buildIndex(t, lang, custom), [t, lang, custom]);
  const results = useMemo(() => search(index, q), [index, q]);
  const logged = useRef(new Set());
  const voice = useVoice(lang, setQ, t, open);
  useEffect(() => {
    if (!open || !start) return;
    if (start.q) setQ(start.q);
    if (start.voice && voice.supported && !voice.listening) voice.toggle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, start]);
  const [popular, setPopular] = useState(FALLBACK);
  useEffect(() => { if (!open) setQ(""); }, [open]);
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    axios.get(`${API}/search-popular`).then((r) => {
      const terms = Array.isArray(r.data?.terms) ? r.data.terms : [];
      if (alive && terms.length) setPopular([...new Set([...terms, ...FALLBACK])].slice(0, 6));
    }).catch(() => {});
    return () => { alive = false; };
  }, [open]);
  useEffect(() => {
    const term = q.trim().toLowerCase();
    if (term.length < 3 || logged.current.has(term)) return undefined;
    const timer = setTimeout(() => {
      logged.current.add(term);
      axios.post(`${API}/search-log`, { q: term, results: results.length, lang }).catch(() => {});
    }, 1500);
    return () => clearTimeout(timer);
  }, [q, results.length, lang]);
  const ask = () => {
    const detail = { project_type: "Altro", message: `${t("search.askMsg", "Ho cercato sul sito")}: “${q.trim()}”. ${t("search.askMsg2", "Vorrei informazioni e un preventivo.")}` };
    onOpenChange(false);
    if (document.getElementById("contatti")) {
      window.dispatchEvent(new CustomEvent("prefill-quote", { detail }));
      return scrollToId("#contatti");
    }
    sessionStorage.setItem("pending-prefill", JSON.stringify(detail));
    return navigate("/#contatti");
  };
  const pick = (to) => {
    onOpenChange(false);
    const [path, hash] = to.split("#");
    if (path === pathname && hash) return scrollToId(`#${hash}`);
    return navigate(to);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="top-[12vh] max-h-[80dvh] w-[calc(100%-1.5rem)] translate-y-0 overflow-hidden border-white/10 bg-[#1B1816] p-0 text-[#F7F5F0] sm:max-w-2xl [&>button]:hidden" data-testid="search-dialog">
        <DialogTitle className="sr-only">{t("search.title", "Cerca nel sito")}</DialogTitle>
        <DialogDescription className="sr-only">{t("search.hint", "Cerca prodotti, progetti, domande frequenti e schede tecniche.")}</DialogDescription>
        <div className="flex items-center gap-3 border-b border-white/10 px-5 py-4">
          <Search className="h-5 w-5 shrink-0 text-[#D98A52]" />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={voice.listening ? t("search.listening", "Ti ascolto…") : t("search.ph", "Cosa stai cercando?")} className="w-full bg-transparent text-lg text-[#F7F5F0] placeholder:text-[#A39E93] focus:outline-none" data-testid="search-input" aria-label={t("search.title", "Cerca nel sito")} />
          <MicButton voice={voice} t={t} />
          <button onClick={() => onOpenChange(false)} className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-white/15 transition-colors duration-200 hover:border-[#D98A52]" data-testid="search-close" aria-label={t("search.close", "Chiudi ricerca")}>
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="max-h-[calc(80dvh-76px)] overflow-y-auto px-2 pb-4" data-testid="search-results">
          <Results results={results} q={q} onPick={pick} onAsk={ask} onSuggest={setQ} popular={popular} t={t} />
        </div>
      </DialogContent>
    </Dialog>
  );
};
